import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, TextField, Typography } from '@mui/material';
import { BackspaceIcon, PhoneIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { getIsNativePlatform } from 'utils/platform';

import { makeOutgoingCallAction, setDialedNumberAction } from 'store/system/actions';
import {
  selectDialedNumber,
  selectHasTelnyxEverConnected,
  selectIsTelnyxConnected,
} from 'store/system/selectors';

import {
  bottomRowStyles,
  callButtonStyles,
  deleteButtonStyles,
  dialerContainerStyles,
  numberInputStyles,
  numpadButtonStyles,
  numpadDigitStyles,
  numpadGridStyles,
  numpadLettersStyles,
} from './Dialer.styles';
import { DialerTestIds } from './Dialer.testIds';

const NUMPAD_KEYS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

const LETTER_TO_DIGIT_MAP = {
  a: '2',
  b: '2',
  c: '2',
  d: '3',
  e: '3',
  f: '3',
  g: '4',
  h: '4',
  i: '4',
  j: '5',
  k: '5',
  l: '5',
  m: '6',
  n: '6',
  o: '6',
  p: '7',
  q: '7',
  r: '7',
  s: '7',
  t: '8',
  u: '8',
  v: '8',
  w: '9',
  x: '9',
  y: '9',
  z: '9',
};

const DIALABLE_CHARS_REGEX = /[^0-9*#+]/g;

const convertLettersToDigits = value =>
  value
    .split('')
    .map(char => LETTER_TO_DIGIT_MAP[char.toLowerCase()] ?? char)
    .join('')
    .replace(DIALABLE_CHARS_REGEX, '');

const disconnectedMessageStyles = { textAlign: 'center', color: 'error.main' };

const NumpadButton = memo(({ digit, letters, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(digit);
  }, [digit, onClick]);

  return (
    <Box component="button" sx={numpadButtonStyles} type="button" onClick={handleClick}>
      <Box component="span" sx={numpadDigitStyles}>
        {digit}
      </Box>
      <Box component="span" sx={numpadLettersStyles}>
        {letters || '\u00A0'}
      </Box>
    </Box>
  );
});

NumpadButton.displayName = 'NumpadButton';

NumpadButton.propTypes = {
  digit: PropTypes.string.isRequired,
  letters: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const Dialer = () => {
  const dispatch = useDispatch();
  const dialedNumber = useSelector(selectDialedNumber);
  const isTelnyxConnected = useSelector(selectIsTelnyxConnected);
  const hasTelnyxEverConnected = useSelector(selectHasTelnyxEverConnected);

  // Use a ref to access the latest dialedNumber inside stable callbacks
  // so that onNumpadClick/onDeleteClick don't depend on dialedNumber directly.
  const dialedNumberRef = useRef(dialedNumber);

  dialedNumberRef.current = dialedNumber;

  // On native the SIP client connects on-demand at dial time (lazy registration), so the call
  // button must NOT require a standing connection — otherwise it never enables (we only connect
  // when a call starts). Web keeps a persistent connection, so it still gates on it.
  const isNative = getIsNativePlatform();

  const isCallDisabled = !dialedNumber.length || (!isNative && !isTelnyxConnected);

  const onChangeInput = useCallback(
    e => {
      const converted = convertLettersToDigits(e.target.value);

      dispatch(setDialedNumberAction(converted));
    },
    [dispatch],
  );

  const onNumpadClick = useCallback(
    digit => {
      dispatch(setDialedNumberAction(dialedNumberRef.current + digit));
    },
    [dispatch],
  );

  const onDeleteClick = useCallback(() => {
    dispatch(setDialedNumberAction(dialedNumberRef.current.slice(0, -1)));
  }, [dispatch]);

  const onCallClick = useCallback(() => {
    if (!dialedNumberRef.current.length || (!isNative && !isTelnyxConnected)) {
      return;
    }

    dispatch(
      makeOutgoingCallAction({
        destinationNumber: dialedNumberRef.current,
      }),
    );
  }, [dispatch, isNative, isTelnyxConnected]);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        onCallClick();

        return;
      }

      if (e.key === 'Backspace' && dialedNumberRef.current.length > 0) {
        e.preventDefault();
        dispatch(setDialedNumberAction(dialedNumberRef.current.slice(0, -1)));

        return;
      }

      // Support typing digits, *, #, + directly via keyboard
      if (/^[0-9*#+]$/.test(e.key)) {
        e.preventDefault();
        dispatch(setDialedNumberAction(dialedNumberRef.current + e.key));
      }
    },
    [dispatch, onCallClick],
  );

  const numpadButtons = useMemo(
    () =>
      NUMPAD_KEYS.map(key => (
        <NumpadButton
          digit={key.digit}
          key={key.digit}
          letters={key.letters}
          onClick={onNumpadClick}
        />
      )),
    [onNumpadClick],
  );

  return (
    <Box sx={dialerContainerStyles} onKeyDown={onKeyDown}>
      <TextField
        autoFocus
        fullWidth
        aria-label="Phone number"
        data-testid={DialerTestIds.NUMBER}
        placeholder="Enter number"
        size="small"
        sx={numberInputStyles}
        value={dialedNumber}
        onChange={onChangeInput}
      />

      <Box sx={numpadGridStyles}>{numpadButtons}</Box>

      <Box sx={bottomRowStyles}>
        <Box />
        <IconButton
          aria-label="Call"
          data-testid={DialerTestIds.CALL}
          disabled={isCallDisabled}
          sx={callButtonStyles}
          onClick={onCallClick}
        >
          <PhoneIcon />
        </IconButton>
        {dialedNumber.length > 0 ? (
          <IconButton
            aria-label="Delete"
            data-testid={DialerTestIds.DELETE}
            sx={deleteButtonStyles}
            onClick={onDeleteClick}
          >
            <BackspaceIcon />
          </IconButton>
        ) : (
          <Box />
        )}
      </Box>

      {hasTelnyxEverConnected && !isTelnyxConnected && (
        <Typography sx={disconnectedMessageStyles} variant="regular12">
          Not connected to telephony service
        </Typography>
      )}
    </Box>
  );
};

export default memo(Dialer);
